<script>
export default {
  name: "DailyBudgetRemaining",
  props: ['dailyRemainingBudget', 'dailyBudget', 'totalDebits', 'totalCredits']
}
</script>

<template>
  <div class="daily-budget-card card budget-positive">

    <!-- Card Header -->
    <div class="card-header">
      <div class="header-icon">
        <i class="bi bi-calendar2-week"></i>
      </div>
      <div class="header-content">
        <h3 class="card-title">Daily Budget</h3>
        <p class="card-subtitle">Adjusted for spending</p>
      </div>
    </div>

    <!-- Card Body -->
    <div class="card-body">
      <div class="row mb-2">
        <div class="col">
          <label for="transactionDescription" class="form-label fw-semibold">
            <i class="fa-solid fa-dollar-sign me-1"></i>Left to Spend
          </label>
        </div>
      </div>
      <!-- Main Amount Display -->
      <div class="amount-section">
        <div class="amount-container">
          <span class="amount-value">{{ dailyRemainingBudget.toFixed(2) }}</span>
        </div>
      </div>
      <div class="row">
        <div class="col">
          <label for="transactionDescription" class="form-label fw-semibold">
            <i class="fa-solid fa-down-left-and-up-right-to-center me-1"></i>Month's Expenses
          </label>
        </div>
      </div>
      <div class="row">
        <div class="col">
          <span class="stat-value expense-value">${{ totalDebits.toFixed(2) }}</span>
        </div>
      </div>
      <div class="row mt-3">
        <div class="col">
          <label for="transactionDescription" class="form-label fw-semibold">
            <i class="fa-solid fa-up-right-and-down-left-from-center me-1"></i>Month's Income
          </label>
        </div>
      </div>
      <div class="row">
        <div class="col">
          <span class="stat-value income-value">${{ totalCredits.toFixed(2) }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.daily-budget-card {
  border-top: 7px solid var(--light-border);
  border-radius: 5px;
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  position: relative;
}

.daily-budget-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
}

.daily-budget-card.budget-negative::before {
  background: linear-gradient(90deg, var(--light-danger), #ff6b6b);
}

/* Card Header */
.card-header {
  background: none;
  border: none;
  padding: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 1rem;
  margin: 1rem;
  border-bottom: 1px solid var(--light-border);
}

.header-icon {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--light-text);
  font-size: 1.25rem;
  flex-shrink: 0;
  border: 1px solid var(--light-text);
}

.budget-negative .header-icon {
  background: linear-gradient(135deg, var(--light-danger), #ff6b6b);
}

.header-content {
  flex: 1;
  text-align: left;
}

.card-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--light-text);
  margin: 0;
  line-height: 1.2;
}

.card-subtitle {
  font-size: 0.85rem;
  color: var(--light-text-muted);
  margin: 0.25rem 0 0;
  line-height: 1.2;
}

/* Card Body */
.card-body {
  padding: 0 1rem 1rem 1rem;
}

/* Amount Section */
.amount-section {
  margin-bottom: 1rem;
}

.amount-container {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 0.25rem;
  margin-bottom: 0.75rem;
}

.currency-symbol {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--light-text-muted);
}

.amount-value {
  font-size: 3.5rem;
  font-weight: 800;
  color: var(--light-text);
  line-height: 1;
}

.budget-positive .amount-value {
  color: var(--light-text);
}

.budget-negative .amount-value {
  color: var(--light-danger);
}

.amount-status {
  margin-top: 0.5rem;
}



/* Statistics Section */
.stats-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 12px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
}

.stat-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  flex-shrink: 0;
}

.expense-icon {
  background: rgba(220, 53, 69, 0.1);
  color: var(--light-danger);
}

.income-icon {
  background: rgba(40, 167, 69, 0.1);
  color: var(--light-success);
}

.stat-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
}

.stat-label {
  font-size: 0.75rem;
  color: var(--light-text-muted);
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  line-height: 1;
}

.stat-value {
  font-size: 2rem;
  font-weight: 700;
  color: var(--light-text);
}

.expense-value {
  color: var(--light-danger);
}

.income-value {
  color: var(--light-success);
}

.stat-divider {
  width: 1px;
  height: 40px;
  background: var(--light-border);
  margin: 0 1rem;
  flex-shrink: 0;
}

/* Progress Section */
.progress-section {
  margin-top: 1rem;
}

.progress-bar-container {
  width: 100%;
  height: 8px;
  background: var(--light-border);
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 0.75rem;
}

.progress-bar {
  height: 100%;
  border-radius: 4px;
  transition: all 0.6s ease;
  background: var(--light-success);
}

.progress-labels {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.progress-label {
  font-size: 0.8rem;
  color: var(--light-text-muted);
  font-weight: 500;
}

.progress-percentage {
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--light-text);
}

/* Dark Mode Adjustments */
:global([data-theme]) .stats-section {
  background: rgba(255, 255, 255, 0.05);
}

:global([data-theme]) .progress-bar-container {
  background: var(--light-border);
}

/* Responsive Design */
@media (max-width: 768px) {
  .header-icon {
    font-size: 1.1rem;
  }

  .card-title {
    font-size: 1rem;
  }

  .card-subtitle {
    font-size: 0.8rem;
  }

  .currency-symbol {
    font-size: 1.25rem;
  }

  .stats-section {
    flex-direction: column;
    gap: 1rem;
    padding: 0.75rem;
  }

  .stat-item {
    width: 100%;
    justify-content: center;
  }

  .stat-divider {
    width: 100%;
    height: 1px;
    margin: 0;
  }

  .stat-content {
    align-items: center;
    text-align: center;
  }
}
</style>